import sessionReader from '../store/readHelpers/SessionReader';
import localCacheService from './LocalCache';

let instance = null;

/**
 * @param {array} unwantedList The substrings we do not want to find in str.
 * @param {string} str
 * @return {boolean}
 */
function doesNotIncludeAnyOf(unwantedList, str) {
    return !unwantedList.some(unwantedItem => str.includes(unwantedItem));
}

export class StateCache {
    static setInstance(newInstance) {
        instance = newInstance;
    }

    constructor() {
        this.localCache = localCacheService.getLocalCacheInstance();
        this.questionSetIdentifier = sessionReader.getQuestionSetIdentifier();
    }

    setQuestionResponse(contentRevisionId, questionResponse) {
        const clonedResponse = Object.assign({}, questionResponse);
        const key = this.getKey('response', contentRevisionId);
        const json = JSON.stringify(clonedResponse);

        this.localCache.set(key, json);
    }

    getQuestionResponse(contentRevisionId) {
        const key = this.getKey('response', contentRevisionId);
        const cachedResponse = this.localCache.get(key);

        if (cachedResponse) {
            return JSON.parse(cachedResponse);
        }

        return null;
    }

    removeQuestionResponse(contentRevisionId) {
        const key = this.getKey('response', contentRevisionId);
        this.localCache.remove(key);
    }

    removeAllCachedTestData() {
        const keysToKeep = [
            'parentChildTestInfo',
            'parentTime',
            'isInitialIdVerificationCompleted',
            'isRoomScanConfirmed',
            'readinessCheckComplete',
        ];
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefixForTest();
        const keys = this
            .getKeysWithPrefix(allKeys, prefix)
            .filter(key => doesNotIncludeAnyOf(keysToKeep, key));

        keys.forEach((key) => {
            if (this.localCache.get(key)) {
                this.localCache.remove(key);
            }
        });
    }

    getAllQuestionResponses() {
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefix('response');
        const keys = this.getKeysWithPrefix(allKeys, prefix);
        const responses = keys.map((key) => {
            const cachedResponse = this.localCache.get(key);
            return JSON.parse(cachedResponse);
        });

        return responses;
    }

    setFlaggedItems(flaggedItems) {
        const key = this.getKey('flaggedItems');
        const json = JSON.stringify({ flaggedItems });

        this.localCache.set(key, json);
    }

    getFlaggedItems() {
        const key = this.getKey('flaggedItems');
        const cachedResponse = this.localCache.get(key);

        if (cachedResponse) {
            const { flaggedItems } = JSON.parse(cachedResponse);

            return flaggedItems;
        }

        return [];
    }

    setParentChildTestInfo(info) {
        const key = this.getKey('parentChildTestInfo');
        this.localCache.set(key, JSON.stringify(info));
    }

    getParentChildTestInfo() {
        const key = this.getKey('parentChildTestInfo');
        const info = this.localCache.get(key);
        return info ? JSON.parse(info) : undefined;
    }

    setReadinessCheckComplete(contentRevisionId) {
        const key = this.getKey('readinessCheckComplete');
        this.localCache.set(key, JSON.stringify(contentRevisionId));
    }

    isReadinessCheckComplete(contentRevisionId) {
        const key = this.getKey('readinessCheckComplete');
        const storedReadinessId = this.localCache.get(key);
        return !!storedReadinessId && parseInt(storedReadinessId, 10) === contentRevisionId;
    }

    getMessages() {
        // Key matches key used in student dashboard, loggedInUserId always -1 in dashboard (NotificationListModel.js)
        return this.localCache.get('readNotifications_-1');
    }

    setReadMessages(messages) {
        this.localCache.set('readNotifications_-1', messages);
    }

    setDayCodePassword(password) {
        const key = this.getKey('dayCodePassword');
        this.localCache.set(key, password);
    }

    getDayCodePassword() {
        const key = this.getKey('dayCodePassword');
        return this.localCache.get(key);
    }

    removeDayCodePassword() {
        const key = this.getKey('dayCodePassword');
        this.localCache.remove(key);
    }

    setTimeRemaining(timeRemaining) {
        const key = this.getKey('timeRemaining');
        this.localCache.set(key, `${timeRemaining}`);
    }

    getTimeRemaining() {
        const key = this.getKey('timeRemaining');
        const timeRemaining = parseInt(this.localCache.get(key), 10);

        return isNaN(timeRemaining) ? undefined : timeRemaining;
    }

    setParentTime(parentTime) {
        const key = this.getKey('parentTime');
        const json = JSON.stringify(parentTime);
        this.localCache.set(key, json);
    }

    getParentTime() {
        const key = this.getKey('parentTime');

        const parentTime = this.localCache.get(key);
        return parentTime ? JSON.parse(parentTime) : undefined;
    }

    setDeliveryObject(assessmentRunId, deliveryObject) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        const json = JSON.stringify(deliveryObject);
        this.localCache.set(key, json);
    }

    getDeliveryObject(assessmentRunId) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        const deliveryObject = this.localCache.get(key);
        return deliveryObject ? JSON.parse(deliveryObject) : undefined;
    }

    removeDeliveryObject(assessmentRunId) {
        const key = this.getKey(`deliveryObject/${assessmentRunId}`);
        this.localCache.remove(key);
    }

    setDeliveryStatus(assessmentRunId, status) {
        const key = this.getKey(`deliveryStatus/${assessmentRunId}`);
        this.localCache.set(key, status);
    }

    getDeliveryStatus(assessmentRunId) {
        const key = this.getKey(`deliveryStatus/${assessmentRunId}`);
        return this.localCache.get(key);
    }

    setRoomScanConfirmationStatus(isCompleted) {
        const key = this.getKey('isRoomScanConfirmed');
        if (isCompleted) {
            this.localCache.set(key, isCompleted);
        } else {
            this.localCache.remove(key);
        }
    }

    isRoomScanConfirmed() {
        const key = this.getKey('isRoomScanConfirmed');
        return !!this.localCache.get(key);
    }

    setInitialIdVerificationCompletion(isCompleted) {
        const key = this.getKey('isInitialIdVerificationCompleted');

        if (isCompleted) {
            this.localCache.set(key, isCompleted);
        } else {
            this.localCache.remove(key);
        }
    }

    // Temporary fix for RP-1355 to not do ID card step upon relogin.
    isInitialIdVerificationCompleted() {
        const key = this.getKey('isInitialIdVerificationCompleted');
        return !!this.localCache.get(key);
    }

    getAllDeliveryObjectKeys() {
        const allKeys = this.localCache.getAllKeys();
        const prefix = this.getKeyPrefix('deliveryObject');
        return this.getFilteredListOfKeys(allKeys, prefix);
    }

    setQuestionCacheValue(keyName, value) {
        const key = this.getKey(keyName);
        this.localCache.set(key, value);
    }

    removeQuestionCacheValue(keyName) {
        const key = this.getKey(keyName);
        this.localCache.remove(key);
    }

    getQuestionCacheValue(keyName) {
        const key = this.getKey(keyName);
        return this.localCache.get(key);
    }

    getKeyPrefix(type) {
        const keyPrefixForTest = this.getKeyPrefixForTest();
        return `${keyPrefixForTest}${type}/`;
    }

    getKeyPrefixForTest() {
        const { paramName, paramValue } = this.questionSetIdentifier;
        const userId = sessionReader.getUserId();
        const cachePrefix = localCacheService.getCachePrefix();
        return `${cachePrefix}/${userId}/${paramName}/${paramValue}/`;
    }

    getKey(type, id = '_') {
        const prefix = this.getKeyPrefix(type);
        return `${prefix}${id}`;
    }

    getKeysWithPrefix(keys, prefix) {
        return keys.filter(key => key.indexOf(prefix) === 0);
    }
}

const stateCacheService = {
    getStateCacheInstance() {
        if (instance !== null) {
            return instance;
        }

        instance = new StateCache();

        return instance;
    },
};

export default stateCacheService;
